// @flow
import React from 'react';
import { useFormikContext } from 'formik';

import { SelectField } from '../../formik-utils/FormFields';
import { useEstimateRateCardVersion } from '../../custom-hooks/useEstimateRateCardVersion';
import { useIsCCStarterCommitSelected } from '../../custom-hooks/useIsCCStarterCommitSelected';
import { useIsEstimateRenewal } from '../../custom-hooks/useIsEstimateRenewal';

import {
  getAvailableMetricsForEstimateCC,
  getCompleteMetricsList,
  getConfiguredMetricRowsFromFormikValuesForEstimateCC,
  getDimensionLevelConfigArrayFromMetricRowForEstimateCC,
  getIfGivenMetricShouldBeDisabledForEstimateCC,
  getMetricsPathForEstimateCC,
  getUsedMutuallyExclusiveGroupsForEstimateCC,
  getNewDimensionsRowWithDefaultsForEstimateCC,
  getUsedMetricsListForEstimateCC,
} from './utils';

export const getMetricNameOptionsForEstimateCC = (
  rowValues,
  allConfiguredRows,
  rateCardVersion,
  isRenewal,
  hasActiveCCStarterCommit
) => {
  const completeMetricsList = getCompleteMetricsList();
  const chosenMetricName = rowValues.metricName;

  // Case 1: There is already a Metric Name which is selected. In this case, just return that single value for the UI to display it
  if (chosenMetricName) {
    const chosenMetricRow = completeMetricsList.find((x) => x.name === chosenMetricName);
    return [
      {
        name: chosenMetricRow.name,
        display_name: chosenMetricRow.label,
      },
    ];
  }

  // Case 2: When Metric Name is not selected, show all options and disable any used metrics
  // If any RC restriction is provided, factor that in too
  const availableMetricsList = getAvailableMetricsForEstimateCC(rateCardVersion, isRenewal);
  const usedMetricsList = getUsedMetricsListForEstimateCC(allConfiguredRows);

  const usedMutuallyExclusiveGroups = getUsedMutuallyExclusiveGroupsForEstimateCC(usedMetricsList);

  return availableMetricsList.map((x) => ({
    name: x.name,
    display_name: x.label,
    shouldNotBeSelectable: getIfGivenMetricShouldBeDisabledForEstimateCC(
      x,
      usedMetricsList,
      usedMutuallyExclusiveGroups,
      hasActiveCCStarterCommit
    ),
  }));
};

export const CommittedCapacityMetricNameDropdownContainerForEstimateCC = ({
  row,
  dimensionsArrayHelpers,
  index,
}) => {
  const { values: formikValues } = useFormikContext();
  const configuredMetrics = getConfiguredMetricRowsFromFormikValuesForEstimateCC(formikValues);
  const rateCardVersion = useEstimateRateCardVersion();
  const isRenewal = useIsEstimateRenewal();
  const hasActiveCCStarterCommit = useIsCCStarterCommitSelected();

  const handleMetricChange = (event, value) => {
    // Check if there are no existing line items for this metric
    const currentMetricObj = configuredMetrics[index];
    const existingLineItems =
      getDimensionLevelConfigArrayFromMetricRowForEstimateCC(currentMetricObj);

    // If no line items exist, automatically add the first one
    if (existingLineItems.length === 0 && value) {
      const newRow = getNewDimensionsRowWithDefaultsForEstimateCC(value);
      dimensionsArrayHelpers.push(newRow);
    }
  };

  const metricsPath = getMetricsPathForEstimateCC(index);
  const metricNamePath = `${metricsPath}.metricName`;
  const isMetricValueSet = row.metricName != null;

  return (
    <SelectField
      addErrorMessage={false}
      addLabel={false}
      disableOnFormErrors={false}
      disabled={isMetricValueSet}
      fieldDisplayName="Select Metric"
      fieldName={metricNamePath}
      onChange={handleMetricChange}
      options={getMetricNameOptionsForEstimateCC(
        row,
        configuredMetrics,
        rateCardVersion,
        isRenewal,
        hasActiveCCStarterCommit
      )}
    />
  );
};
